// One GameSession per room page: owns the socket and the clock, performs the handshake
// (resume with a saved seat token, reseat with a code, or join with a name), tracks command acks
// by cid, and writes everything it hears into the store.
import type { ClientMsg, CommandResult, ErrorMsg, GameCommand, ServerMsg } from "../protocol";
import { clearSeat, loadSeat, saveName, saveSeat, updateSeatSeq } from "../state/local";
import { useGame, type SessionStatus } from "../state/store";
import { ClockSync } from "./clock";
import { ReconnectingSocket, wsUrl } from "./socket";

const COMMAND_TIMEOUT_MS = 10_000;
const HANDSHAKE_CID = "hs";
// "Try again later" while coming back to a seat: wait as long as the server asks, and longer
// after each refusal in a row, plus a random second so a table does not retry in lockstep
const BUSY_BACKOFF_MIN_MS = 1000;
const BUSY_BACKOFF_MAX_MS = 30_000;
const BUSY_JITTER_MS = 1000;

interface Pending {
  resolve: (result: CommandResult) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** Handshake errors that end the session (the server closes the socket after sending them). */
const TERMINAL_ERRORS: Record<string, SessionStatus> = {
  room_not_found: "not_found",
  game_in_progress: "game_in_progress",
  room_full: "room_full",
};

export class GameSession {
  readonly clock = new ClockSync();
  private readonly socket: ReconnectingSocket;
  private readonly pending = new Map<string, Pending>();
  private cidCounter = 0;
  private seated = false; // the current connection has been welcomed
  private everSeated = false; // some connection in this session was welcomed
  private running = false;
  private pendingName: string | null = null;
  private pendingReseat: string | null = null;
  private handshakeType: ClientMsg["type"] | null = null; // the handshake in flight
  private busyRetries = 0; // "try again later" replies in a row while resuming

  constructor(readonly code: string) {
    this.socket = new ReconnectingSocket(
      wsUrl(code),
      {
        onOpen: () => this.handshake(),
        onMessage: (msg) => this.onMessage(msg),
        onClose: () => this.onClose(),
      },
      () => ({ type: "ping", client_ts: Date.now() }),
    );
  }

  private get store() {
    return useGame.getState();
  }

  /** Connect if this device already holds a seat here; otherwise ask for a name. */
  start(): void {
    if (loadSeat(this.code)) this.connect();
    else this.store.setStatus("need_name");
  }

  stop(): void {
    this.running = false;
    this.socket.stop();
    this.failPending("offline", "Disconnected.");
  }

  join(name: string): void {
    const trimmed = name.trim();
    saveName(trimmed);
    this.pendingName = trimmed;
    this.pendingReseat = null;
    this.store.setJoinError(null);
    this.store.setJoinMode("name");
    this.connect();
  }

  reseat(code: string): void {
    this.pendingReseat = code.trim();
    this.pendingName = null;
    this.store.setJoinError(null);
    this.store.setJoinMode("reseat");
    this.connect();
  }

  /** After the seat moved to another phone: open the join page on the rejoin code form. */
  enterRejoinCode(): void {
    this.store.setJoinMode("reseat");
    this.store.setStatus("need_name");
  }

  send(cmd: GameCommand): Promise<CommandResult> {
    const cid = this.nextCid();
    return this.request(cid, { type: "cmd", cid, cmd });
  }

  issueReseatCode(targetSeatId: string): Promise<CommandResult> {
    const cid = this.nextCid();
    return this.request(cid, {
      type: "room_cmd",
      cid,
      action: "issue_reseat_code",
      target_seat_id: targetSeatId,
    });
  }

  /** Current server time in epoch ms, for countdowns against server deadlines. */
  serverNow(): number {
    return this.clock.serverNow();
  }

  // --- internals -------------------------------------------------------------------------------
  private connect(): void {
    // not "reconnecting", which keeps showing the last screen: that screen is out of date
    this.store.setStatus("connecting");
    if (this.socket.isOpen) {
      this.handshake();
      return;
    }
    if (!this.running) {
      this.running = true;
      this.socket.start();
    }
  }

  private nextCid(): string {
    this.cidCounter += 1;
    return `c${this.cidCounter}`;
  }

  private request(cid: string, msg: ClientMsg): Promise<CommandResult> {
    if (!this.seated || !this.socket.send(msg)) {
      return Promise.resolve(clientError(cid, "offline", "Not connected. Reconnecting…"));
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(cid);
        resolve(clientError(cid, "timeout", "No answer from the server."));
      }, COMMAND_TIMEOUT_MS);
      this.pending.set(cid, { resolve, timer });
    });
  }

  private handshake(): void {
    const saved = loadSeat(this.code);
    let msg: ClientMsg | null = null;
    if (saved) {
      const since = Math.max(this.store.seq, saved.seq);
      msg = { type: "resume", cid: HANDSHAKE_CID, seat_token: saved.token, since };
    } else if (this.pendingReseat) {
      msg = { type: "reseat", cid: HANDSHAKE_CID, reseat_code: this.pendingReseat };
    } else if (this.pendingName) {
      msg = { type: "join", cid: HANDSHAKE_CID, name: this.pendingName };
    }
    if (msg === null) {
      this.halt("need_name");
      return;
    }
    this.handshakeType = msg.type;
    this.socket.send(msg);
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.kind) {
      case "welcome":
        this.onWelcome(msg);
        break;
      case "state":
        if (this.store.applyState(msg)) updateSeatSeq(this.code, msg.seq);
        break;
      case "ack":
      case "error": {
        const waiter = msg.cid ? this.pending.get(msg.cid) : undefined;
        if (waiter && msg.cid) {
          clearTimeout(waiter.timer);
          this.pending.delete(msg.cid);
          waiter.resolve(msg);
        } else if (msg.kind === "error" && !this.seated) {
          this.onHandshakeError(msg);
        } else if (msg.kind === "error") {
          this.store.showToast(msg.message, "error");
        }
        break;
      }
      case "pong":
        this.clock.addSample(msg.client_ts, msg.server_ts, Date.now());
        this.store.setOffset(this.clock.offsetMs);
        break;
      case "bye":
        this.onBye(msg.reason);
        break;
    }
  }

  private onWelcome(msg: Extract<ServerMsg, { kind: "welcome" }>): void {
    this.seated = true;
    this.everSeated = true;
    if (msg.seat_token) {
      // a new token (join or reseat): remember it so this phone can always come back
      const name = this.pendingName ?? loadSeat(this.code)?.name ?? "";
      saveSeat(this.code, { token: msg.seat_token, seatId: msg.seat_id, name, seq: 0 });
    }
    this.pendingName = null;
    this.pendingReseat = null;
    this.handshakeType = null;
    this.busyRetries = 0;
    this.store.applyWelcome(msg);
    updateSeatSeq(this.code, msg.state.seq);
    this.store.setStatus("live");
    this.syncClock();
  }

  private onHandshakeError(err: ErrorMsg): void {
    const resuming = this.handshakeType === "resume";
    this.handshakeType = null;
    if (err.code === "rate_limited" && resuming) {
      // a whole table on one network can come back at once, after a deploy or a Wi-Fi blip:
      // keep the seat and try again when the server says, never drop to the join form
      this.retryLater(err.retry_after_ms ?? 0);
      return;
    }
    if (err.code === "seat_moved") {
      this.seatMoved();
      return;
    }
    // the room has closed (rooms are restored before a restarted server takes connections, so
    // this is never a blip): forget it, or it stays under Continue on the home screen for good
    if (err.code === "room_not_found") clearSeat(this.code);
    const terminal = TERMINAL_ERRORS[err.code];
    if (terminal) {
      this.halt(terminal);
      return;
    }
    if (err.code === "invalid_token") {
      // the saved seat is gone (removed while away, or the room was reset): start fresh
      clearSeat(this.code);
      this.halt("need_name");
      return;
    }
    // name taken, wrong rejoin code, rate limited...: back to the form with the reason
    this.store.setJoinError(err.message);
    this.halt("need_name");
  }

  private onBye(reason: string): void {
    if (reason === "kicked" || reason === "left") {
      clearSeat(this.code);
      this.halt(reason);
    } else if (reason === "superseded") {
      this.halt("superseded");
    } else if (reason === "moved") {
      this.seatMoved();
    } else {
      // room_closed or invalid_token: the seat is gone for good
      clearSeat(this.code);
      this.halt("closed");
    }
  }

  /** The server cannot take us back yet: the socket reconnects after a wait, seat kept. */
  private retryLater(retryAfterMs: number): void {
    const backoff = Math.min(BUSY_BACKOFF_MAX_MS, BUSY_BACKOFF_MIN_MS * 2 ** this.busyRetries);
    this.busyRetries += 1;
    this.socket.retryIn(Math.max(retryAfterMs, backoff) + Math.random() * BUSY_JITTER_MS);
  }

  /**
   * A rejoin code moved this seat to another phone, so the saved token no longer opens it.
   * Forget the seat and the last screen with it: nothing out of date may show again, and the
   * next try is a new phone's (a rejoin code, or a name in the lobby).
   */
  private seatMoved(): void {
    clearSeat(this.code);
    this.everSeated = false;
    this.halt("moved");
    this.store.reset(this.code);
    this.store.setStatus("moved");
  }

  private onClose(): void {
    this.failPending("offline", "Connection lost. Reconnecting…");
    this.seated = false;
    if (this.running && this.everSeated) this.store.setStatus("reconnecting");
  }

  private halt(status: SessionStatus): void {
    this.running = false;
    this.seated = false;
    this.socket.stop();
    this.failPending("offline", "Disconnected.");
    this.store.setStatus(status);
  }

  private failPending(code: string, message: string): void {
    for (const [cid, p] of this.pending) {
      clearTimeout(p.timer);
      p.resolve(clientError(cid, code, message));
    }
    this.pending.clear();
  }

  private syncClock(): void {
    // a few quick round trips so countdowns are right from the first screen
    for (const delay of [0, 400, 1200]) {
      setTimeout(() => this.socket.send({ type: "ping", client_ts: Date.now() }), delay);
    }
  }
}

/** An error produced on the phone itself (offline, timeout), shaped like a server error. */
export function clientError(cid: string, code: string, message: string): ErrorMsg {
  return { kind: "error", cid, code, message, engine_code: null, retry_after_ms: null };
}
